import { useState, useEffect, useRef } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
function wapi(path, opts = {}) {
  const token = localStorage.getItem("sentinel_token");
  return fetch(`${API_URL}${path}`, {
    ...opts,
    headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(opts.headers || {}) },
  }).then(r => r.json());
}

// ═══════════════════════════════════════════════════════════
//  SENTINEL — CLIENT ONBOARDING WIZARD
//  6 steps: Welcome → Profile → Plan → Target → Scan → Done
// ═══════════════════════════════════════════════════════════

const C = {
  bg:         "#05070d",
  panel:      "#090d18",
  surface:    "#0d1220",
  card:       "#101628",
  border:     "#182035",
  borderGlow: "#1e2d50",
  accent:     "#00f5d4",
  accentDim:  "#00f5d412",
  accentGlow: "#00f5d425",
  accentMid:  "#00c4a9",
  blue:       "#3d8bff",
  blueDim:    "#3d8bff15",
  warn:       "#ffb340",
  warnDim:    "#ffb34015",
  critical:   "#ff4060",
  green:      "#00e5b4",
  gold:       "#f0c040",
  goldDim:    "#f0c04015",
  text:       "#c8daf0",
  dim:        "#6a85a8",
  muted:      "#384a62",
};

const FONT = `@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@400;500;600;700&family=Share+Tech+Mono&family=Exo+2:wght@300;400;500;600;700;800;900&display=swap');`;

const CSS = `
  ${FONT}
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0}
  html,body{height:100%;overflow:hidden}
  body{background:${C.bg};color:${C.text};font-family:'Exo 2',sans-serif}
  input,select,textarea{background:${C.surface};border:1px solid ${C.border};border-radius:5px;color:${C.text};font-family:'Exo 2',sans-serif;font-size:14px;padding:11px 14px;outline:none;transition:all .2s;width:100%}
  input:focus,select:focus{border-color:${C.accent};box-shadow:0 0 0 3px ${C.accentDim}}
  input::placeholder{color:${C.muted}}
  ::-webkit-scrollbar{width:3px}::-webkit-scrollbar-track{background:${C.panel}}::-webkit-scrollbar-thumb{background:${C.borderGlow};border-radius:2px}

  @keyframes fadeUp   {from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}
  @keyframes fadeIn   {from{opacity:0}to{opacity:1}}
  @keyframes slideR   {from{opacity:0;transform:translateX(40px)}to{opacity:1;transform:translateX(0)}}
  @keyframes slideL   {from{opacity:0;transform:translateX(-40px)}to{opacity:1;transform:translateX(0)}}
  @keyframes pulse    {0%,100%{opacity:1}50%{opacity:.25}}
  @keyframes scan     {0%{transform:translateY(-100%)}100%{transform:translateY(600px)}}
  @keyframes spin     {to{transform:rotate(360deg)}}
  @keyframes pop      {0%{transform:scale(.6);opacity:0}70%{transform:scale(1.15)}100%{transform:scale(1);opacity:1}}
  @keyframes glowPulse{0%,100%{box-shadow:0 0 12px ${C.accentGlow}}50%{box-shadow:0 0 32px ${C.accentGlow},0 0 64px ${C.accentDim}}}
  @keyframes drawLine {from{width:0}to{width:100%}}
  @keyframes countUp  {from{opacity:0;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
  @keyframes gridFlash{0%,100%{opacity:.18}50%{opacity:.32}}
  @keyframes ticker   {0%{transform:translateX(0)}100%{transform:translateX(-50%)}}
  @keyframes blink    {0%,100%{opacity:1}49%{opacity:1}50%{opacity:0}99%{opacity:0}}

  .step-enter{animation:slideR .4s cubic-bezier(.25,.46,.45,.94) both}
  .step-exit {animation:slideL .3s ease both}
  .fade-up   {animation:fadeUp .5s ease both}
  .pop       {animation:pop .4s cubic-bezier(.34,1.56,.64,1) both}
  .mono      {font-family:'Share Tech Mono',monospace}
`;

// ── Particle grid background ────────────────────────────────
function GridBg() {
  return (
    <div style={{ position:"fixed",inset:0,pointerEvents:"none",zIndex:0,overflow:"hidden" }}>
      {/* Dot grid */}
      <div style={{ position:"absolute",inset:0,backgroundImage:`radial-gradient(${C.muted}55 1px,transparent 1px)`,backgroundSize:"32px 32px",animation:"gridFlash 4s ease infinite" }} />
      {/* Scanline */}
      <div style={{ position:"absolute",left:0,right:0,height:2,background:`linear-gradient(90deg,transparent,${C.accentGlow},transparent)`,animation:"scan 5s linear infinite",top:0 }} />
      {/* Glow orb top */}
      <div style={{ position:"absolute",top:"-20%",left:"50%",transform:"translateX(-50%)",width:700,height:400,background:`radial-gradient(ellipse,${C.accentDim} 0%,transparent 65%)`,pointerEvents:"none" }} />
      {/* Corner decoration TL */}
      <svg style={{ position:"absolute",top:0,left:0,width:200,height:200,opacity:.25 }} viewBox="0 0 200 200">
        <path d="M0 0 L80 0 L80 2 L2 2 L2 80 L0 80 Z" fill={C.accent} />
        <path d="M20 0 L20 20 L0 20" fill="none" stroke={C.accent} strokeWidth="1" opacity=".5" />
      </svg>
      {/* Corner decoration BR */}
      <svg style={{ position:"absolute",bottom:0,right:0,width:200,height:200,opacity:.25 }} viewBox="0 0 200 200">
        <path d="M200 200 L120 200 L120 198 L198 198 L198 120 L200 120 Z" fill={C.accent} />
      </svg>
    </div>
  );
}

// ── Progress bar across top ─────────────────────────────────
function TopProgress({ step, total }) {
  const pct = ((step) / (total - 1)) * 100;
  return (
    <div style={{ position:"fixed",top:0,left:0,right:0,height:3,background:C.border,zIndex:100 }}>
      <div style={{ height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,${C.accentMid},${C.accent})`,transition:"width .6s cubic-bezier(.4,0,.2,1)",boxShadow:`0 0 12px ${C.accentGlow}` }} />
    </div>
  );
}

// ── Step indicator sidebar ──────────────────────────────────
const STEPS = [
  { label:"Welcome",   icon:"⬡" },
  { label:"Profile",   icon:"◎" },
  { label:"Plan",      icon:"◈" },
  { label:"Target",    icon:"⊞" },
  { label:"Scan",      icon:"◉" },
  { label:"Complete",  icon:"✓" },
];

function StepNav({ current }) {
  return (
    <div style={{ position:"fixed",left:0,top:0,bottom:0,width:220,background:C.panel,borderRight:`1px solid ${C.border}`,display:"flex",flexDirection:"column",justifyContent:"center",padding:"0 20px",zIndex:50 }}>
      {/* Logo */}
      <div style={{ position:"absolute",top:28,left:20,right:20 }}>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}>
          <span style={{ fontSize:22,color:C.accent,filter:`drop-shadow(0 0 6px ${C.accentGlow})` }}>⬡</span>
          <div>
            <div style={{ fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:18,letterSpacing:3,color:C.accent }}>SENTINEL</div>
            <div style={{ fontSize:9,color:C.dim,fontFamily:"'Share Tech Mono',monospace",letterSpacing:2 }}>SETUP WIZARD</div>
          </div>
        </div>
      </div>

      {/* Steps */}
      <div style={{ display:"flex",flexDirection:"column",gap:4 }}>
        {STEPS.map((s,i) => {
          const done    = i < current;
          const active  = i === current;
          const future  = i > current;
          return (
            <div key={i} style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 12px",borderRadius:7,background:active?C.accentDim:"transparent",border:`1px solid ${active?C.accent+"40":"transparent"}`,transition:"all .3s" }}>
              {/* Step circle */}
              <div style={{ width:28,height:28,borderRadius:"50%",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,fontSize:12,fontWeight:700,transition:"all .3s",background:done?C.accent:active?C.accentDim:C.surface,border:`1.5px solid ${done?C.accent:active?C.accent:C.border}`,color:done?C.bg:active?C.accent:C.muted,boxShadow:active?`0 0 14px ${C.accentGlow}`:"none" }}>
                {done ? "✓" : s.icon}
              </div>
              <div>
                <div style={{ fontSize:13,fontWeight:active?700:500,color:done||active?C.text:C.dim,transition:"color .3s" }}>{s.label}</div>
                {active && <div style={{ fontSize:10,color:C.accent,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1,marginTop:1 }}>IN PROGRESS</div>}
              </div>
              {/* Connector line */}
            </div>
          );
        })}
      </div>

      {/* Bottom badge */}
      <div style={{ position:"absolute",bottom:24,left:20,right:20 }}>
        <div style={{ background:C.warnDim,border:`1px solid ${C.warn}35`,borderRadius:6,padding:"10px 12px" }}>
          <div style={{ fontSize:10,color:C.warn,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1,marginBottom:3 }}>⚠ AUTHORIZED USE</div>
          <div style={{ fontSize:10,color:C.dim,lineHeight:1.5 }}>Only test systems you own or have written permission to test.</div>
        </div>
      </div>
    </div>
  );
}

// ── Shared field component ──────────────────────────────────
function Field({ label, hint, error, required, children }) {
  return (
    <div style={{ marginBottom:18 }}>
      <div style={{ display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:7 }}>
        <label style={{ fontSize:11,color:C.dim,fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,textTransform:"uppercase" }}>
          {label}{required&&<span style={{ color:C.critical }}> *</span>}
        </label>
        {hint && <span style={{ fontSize:11,color:C.muted }}>{hint}</span>}
      </div>
      {children}
      {error && <div style={{ fontSize:11,color:C.critical,marginTop:4,fontFamily:"'Share Tech Mono',monospace" }}>⚠ {error}</div>}
    </div>
  );
}

// ── Big action button ───────────────────────────────────────
function NextBtn({ children, onClick, disabled, loading, variant="primary" }) {
  const bg = variant==="primary" ? C.accent : "transparent";
  const col = variant==="primary" ? C.bg : C.accent;
  const border = variant==="primary" ? "none" : `1.5px solid ${C.accent}`;
  return (
    <button onClick={!disabled&&!loading?onClick:undefined} style={{ display:"inline-flex",alignItems:"center",justifyContent:"center",gap:8,padding:"13px 32px",background:bg,color:col,border,borderRadius:6,fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:16,letterSpacing:1.5,cursor:disabled||loading?"not-allowed":"pointer",opacity:disabled||loading?.5:1,transition:"all .2s",boxShadow:variant==="primary"?`0 0 24px ${C.accentGlow}`:"none",textTransform:"uppercase" }}>
      {loading ? <div style={{ width:18,height:18,border:`2px solid ${C.bg}33`,borderTopColor:C.bg,borderRadius:"50%",animation:"spin .7s linear infinite" }} /> : null}
      {children}
    </button>
  );
}

// ═══════════════════════════════════════════════════════════
//  STEP 0 — WELCOME
// ═══════════════════════════════════════════════════════════
function StepWelcome({ data, onNext }) {
  const [shown, setShown] = useState(false);
  useEffect(() => { setTimeout(()=>setShown(true),100); }, []);

  return (
    <div className="step-enter" style={{ maxWidth:640,margin:"0 auto" }}>
      {/* Big icon */}
      <div style={{ textAlign:"center",marginBottom:48 }}>
        <div style={{ display:"inline-block",width:100,height:100,borderRadius:"50%",background:C.accentDim,border:`2px solid ${C.accent}50`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:48,color:C.accent,margin:"0 auto 28px",animation:"glowPulse 3s ease infinite",filter:`drop-shadow(0 0 20px ${C.accentGlow})` }}>⬡</div>
        <h1 style={{ fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:48,letterSpacing:3,color:C.text,marginBottom:8,lineHeight:1 }}>
          WELCOME TO<br /><span style={{ color:C.accent,filter:`drop-shadow(0 0 12px ${C.accentGlow})` }}>SENTINEL</span>
        </h1>
        <p style={{ fontSize:16,color:C.dim,lineHeight:1.7,marginTop:16 }}>
          Hi <strong style={{ color:C.text }}>{data.full_name || "there"}</strong>! Let's get you set up in under 3 minutes.<br />
          We'll walk you through your profile, plan, and first scan.
        </p>
      </div>

      {/* What you'll do */}
      <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:12,marginBottom:48 }}>
        {[
          { icon:"◎", step:"01", title:"Add a Target",     desc:"Tell us what system to test — IP or hostname." },
          { icon:"◉", step:"02", title:"Run a Scan",       desc:"We launch Nmap, detect services and CVEs." },
          { icon:"⊟", step:"03", title:"Get Your Report",  desc:"PDF report delivered to your inbox." },
        ].map((item,i)=>(
          <div key={i} className="fade-up" style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:10,padding:20,textAlign:"center",animation:`fadeUp .5s ${.1+i*.12}s both` }}>
            <div style={{ fontSize:28,color:C.accent,marginBottom:10 }}>{item.icon}</div>
            <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:C.muted,letterSpacing:2,marginBottom:6 }}>STEP {item.step}</div>
            <div style={{ fontWeight:700,fontSize:14,marginBottom:6 }}>{item.title}</div>
            <div style={{ fontSize:12,color:C.dim,lineHeight:1.6 }}>{item.desc}</div>
          </div>
        ))}
      </div>

      {/* Time estimate */}
      <div style={{ display:"flex",alignItems:"center",gap:16,background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"14px 20px",marginBottom:36 }}>
        <span style={{ fontSize:24,color:C.accent }}>◷</span>
        <div>
          <div style={{ fontWeight:600,fontSize:14 }}>Estimated setup time: <span style={{ color:C.accent }}>~3 minutes</span></div>
          <div style={{ fontSize:12,color:C.dim }}>You can pause and return anytime. Progress is saved automatically.</div>
        </div>
      </div>

      <div style={{ display:"flex",gap:12 }}>
        <NextBtn onClick={onNext}>Let's Begin →</NextBtn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  STEP 1 — PROFILE
// ═══════════════════════════════════════════════════════════
function StepProfile({ data, setData, onNext, onBack }) {
  const [errors, setErrors] = useState({});

  const validate = () => {
    const e = {};
    if (!data.full_name?.trim())   e.full_name   = "Full name is required";
    if (!data.company?.trim())     e.company     = "Company name is required";
    if (!data.phone?.trim())       e.phone       = "Phone number is required";
    if (!data.industry)            e.industry    = "Please select an industry";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  return (
    <div className="step-enter" style={{ maxWidth:580,margin:"0 auto" }}>
      <div style={{ marginBottom:36 }}>
        <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:C.accent,letterSpacing:3,marginBottom:10 }}>STEP 01 / 05</div>
        <h2 style={{ fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:38,letterSpacing:2,marginBottom:8 }}>YOUR PROFILE</h2>
        <p style={{ color:C.dim,fontSize:15 }}>Help us personalise your experience and reports.</p>
      </div>

      <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:28 }}>
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px" }}>
          <Field label="Full Name" required error={errors.full_name}>
            <input placeholder="Jane Smith" value={data.full_name||""} onChange={e=>setData(p=>({...p,full_name:e.target.value}))} />
          </Field>
          <Field label="Job Title" hint="optional">
            <input placeholder="Security Manager" value={data.job_title||""} onChange={e=>setData(p=>({...p,job_title:e.target.value}))} />
          </Field>
        </div>

        <Field label="Company / Organisation" required error={errors.company}>
          <input placeholder="Acme Corporation" value={data.company||""} onChange={e=>setData(p=>({...p,company:e.target.value}))} />
        </Field>

        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px" }}>
          <Field label="Phone Number" required error={errors.phone}>
            <input placeholder="+27 82 000 0000" value={data.phone||""} onChange={e=>setData(p=>({...p,phone:e.target.value}))} />
          </Field>
          <Field label="Industry" required error={errors.industry}>
            <select value={data.industry||""} onChange={e=>setData(p=>({...p,industry:e.target.value}))}>
              <option value="">Select industry...</option>
              {["Finance & Banking","Healthcare","Retail & E-commerce","Government","Technology","Manufacturing","Hospitality","Education","Legal","Other"].map(i=><option key={i} value={i}>{i}</option>)}
            </select>
          </Field>
        </div>

        <Field label="How did you hear about us?" hint="optional">
          <select value={data.source||""} onChange={e=>setData(p=>({...p,source:e.target.value}))}>
            <option value="">Select...</option>
            {["Google Search","LinkedIn","Referral from colleague","Social media","Industry event","Cold email"].map(s=><option key={s} value={s}>{s}</option>)}
          </select>
        </Field>
      </div>

      <div style={{ display:"flex",gap:12,marginTop:28 }}>
        <NextBtn onClick={async ()=>{
          if (!validate()) return;
          try {
            await wapi("/api/auth/profile", { method:"PUT", body: JSON.stringify({
              full_name: data.full_name,
              company_name: data.company,
              phone: data.phone,
            })});
          } catch(e) { /* non-blocking — continue anyway */ }
          onNext();
        }}>Continue →</NextBtn>
        <NextBtn variant="outline" onClick={onBack}>← Back</NextBtn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  STEP 2 — CHOOSE PLAN
// ═══════════════════════════════════════════════════════════
const PLANS = [
  { slug:"starter",    name:"Starter",    price:499,  targets:5,  scans:10, desc:"Perfect for small businesses with a few systems to monitor.", features:["5 targets","10 scans/month","Email reports","Vulnerability scoring"] },
  { slug:"business",   name:"Business",   price:1499, targets:25, scans:50, desc:"Ideal for growing teams managing multiple client environments.", features:["25 targets","50 scans/month","PDF reports","CVE tracking","API access"], popular:true },
  { slug:"enterprise", name:"Enterprise", price:4999, targets:-1, scans:-1, desc:"Unlimited scale for large organisations and MSPs.", features:["Unlimited targets","Unlimited scans","White-label reports","Dedicated support"] },
];

function StepPlan({ data, setData, onNext, onBack }) {
  const [hovered, setHovered] = useState(null);
  const selected = data.plan || "business";

  return (
    <div className="step-enter" style={{ maxWidth:820,margin:"0 auto" }}>
      <div style={{ marginBottom:36 }}>
        <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:C.accent,letterSpacing:3,marginBottom:10 }}>STEP 02 / 05</div>
        <h2 style={{ fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:38,letterSpacing:2,marginBottom:8 }}>CHOOSE YOUR PLAN</h2>
        <p style={{ color:C.dim,fontSize:15 }}>All plans start with a 14-day free trial. Cancel anytime. Billed in ZAR via PayFast.</p>
      </div>

      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:14,marginBottom:28 }}>
        {PLANS.map((plan,i)=>{
          const isSelected = selected===plan.slug;
          const isHovered  = hovered===plan.slug;
          return (
            <div key={plan.slug} onClick={()=>setData(p=>({...p,plan:plan.slug}))}
              onMouseEnter={()=>setHovered(plan.slug)} onMouseLeave={()=>setHovered(null)}
              style={{ background:C.card,border:`2px solid ${isSelected?C.accent:isHovered?C.borderGlow:C.border}`,borderRadius:12,padding:24,cursor:"pointer",position:"relative",transition:"all .2s",transform:isSelected?"translateY(-4px)":"none",boxShadow:isSelected?`0 8px 32px ${C.accentGlow}`:"none",animation:`fadeUp .45s ${i*.1}s both` }}>
              {plan.popular && <div style={{ position:"absolute",top:-12,left:"50%",transform:"translateX(-50%)",background:C.accent,color:C.bg,fontFamily:"'Share Tech Mono',monospace",fontSize:10,fontWeight:700,letterSpacing:2,padding:"4px 14px",borderRadius:20 }}>POPULAR</div>}
              {/* Selected checkmark */}
              {isSelected && <div style={{ position:"absolute",top:14,right:14,width:22,height:22,borderRadius:"50%",background:C.accent,display:"flex",alignItems:"center",justifyContent:"center",fontSize:11,color:C.bg,fontWeight:700,animation:"pop .3s ease" }}>✓</div>}
              <div style={{ marginBottom:6 }}>
                <span style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:isSelected?C.accent:C.muted,letterSpacing:2,textTransform:"uppercase" }}>{plan.name}</span>
              </div>
              <div style={{ fontFamily:"'Rajdhani',sans-serif",fontSize:44,fontWeight:700,color:isSelected?C.accent:C.text,lineHeight:1,marginBottom:4 }}>R{plan.price}</div>
              <div style={{ fontSize:11,color:C.dim,marginBottom:16 }}>/ month · billed monthly</div>
              <div style={{ fontSize:12,color:C.dim,marginBottom:16,lineHeight:1.6 }}>{plan.desc}</div>
              <div style={{ borderTop:`1px solid ${C.border}`,paddingTop:14 }}>
                {plan.features.map((f,j)=>(
                  <div key={j} style={{ display:"flex",alignItems:"center",gap:8,fontSize:12,color:isSelected?C.text:C.dim,marginBottom:8 }}>
                    <span style={{ color:isSelected?C.accent:C.muted,fontSize:11 }}>✓</span>{f}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <div style={{ background:C.surface,border:`1px solid ${C.border}`,borderRadius:8,padding:"12px 18px",marginBottom:28,display:"flex",alignItems:"center",gap:12,fontSize:13,color:C.dim }}>
        <span style={{ color:C.accent,fontSize:18 }}>◈</span>
        <span>Payments processed securely by <strong style={{ color:C.text }}>PayFast</strong> · PCI DSS Level 1 · No hidden fees · 3.5% transaction fee deducted by PayFast</span>
      </div>

      <div style={{ display:"flex",gap:12 }}>
        <NextBtn onClick={onNext}>Continue with {PLANS.find(p=>p.slug===selected)?.name} →</NextBtn>
        <NextBtn variant="outline" onClick={onBack}>← Back</NextBtn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  STEP 3 — ADD FIRST TARGET
// ═══════════════════════════════════════════════════════════
function StepTarget({ data, setData, onNext, onBack }) {
  const [errors, setErrors]   = useState({});
  const [agreed, setAgreed]   = useState(false);
  const [ipType, setIpType]   = useState("ip");

  const validate = () => {
    const e = {};
    const t = data.target || {};
    if (!t.name?.trim())          e.name = "Target name required";
    if (!t.ip?.trim())            e.ip   = "IP or hostname required";
    if (!t.authorized_by?.trim()) e.auth = "Authorization contact required";
    if (!agreed)                  e.agreed = "You must confirm authorization to proceed";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (key, val) => setData(p=>({...p, target:{...(p.target||{}), [key]:val}}));
  const t = data.target || {};

  return (
    <div className="step-enter" style={{ maxWidth:600,margin:"0 auto" }}>
      <div style={{ marginBottom:36 }}>
        <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:C.accent,letterSpacing:3,marginBottom:10 }}>STEP 03 / 05</div>
        <h2 style={{ fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:38,letterSpacing:2,marginBottom:8 }}>ADD YOUR FIRST TARGET</h2>
        <p style={{ color:C.dim,fontSize:15 }}>A target is any system, server, or network you're authorised to test.</p>
      </div>

      <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:28,marginBottom:16 }}>
        <Field label="Target Name" required error={errors.name} hint="Give it a memorable label">
          <input placeholder="e.g. Production Web Server, Office Network" value={t.name||""} onChange={e=>set("name",e.target.value)} />
        </Field>

        {/* IP / hostname toggle */}
        <div style={{ marginBottom:18 }}>
          <div style={{ display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:7 }}>
            <label style={{ fontSize:11,color:C.dim,fontFamily:"'Share Tech Mono',monospace",letterSpacing:2,textTransform:"uppercase" }}>TARGET ADDRESS <span style={{ color:C.critical }}>*</span></label>
          </div>
          <div style={{ display:"flex",gap:6,marginBottom:8 }}>
            {["ip","hostname","range"].map(t=>(
              <button key={t} onClick={()=>setIpType(t)} style={{ padding:"5px 14px",borderRadius:4,border:`1px solid ${ipType===t?C.accent:C.border}`,background:ipType===t?C.accentDim:"transparent",color:ipType===t?C.accent:C.dim,fontSize:11,fontFamily:"'Share Tech Mono',monospace",cursor:"pointer",letterSpacing:1,transition:"all .15s" }}>
                {t.toUpperCase()}
              </button>
            ))}
          </div>
          <input
            placeholder={ipType==="ip"?"192.168.1.1":ipType==="hostname"?"web01.yourcompany.co.za":"192.168.1.0/24"}
            value={t.ip||""} onChange={e=>set("ip",e.target.value)}
          />
          {errors.ip && <div style={{ fontSize:11,color:C.critical,marginTop:4,fontFamily:"'Share Tech Mono',monospace" }}>⚠ {errors.ip}</div>}
        </div>

        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0 20px" }}>
          <Field label="Hostname" hint="optional">
            <input placeholder="web01.company.co.za" value={t.hostname||""} onChange={e=>set("hostname",e.target.value)} />
          </Field>
          <Field label="Description" hint="optional">
            <input placeholder="Main customer-facing website" value={t.description||""} onChange={e=>set("description",e.target.value)} />
          </Field>
        </div>

        <Field label="Authorised By" required error={errors.auth} hint="Name of system owner or who gave permission">
          <input placeholder="John Smith (CTO)" value={t.authorized_by||""} onChange={e=>set("authorized_by",e.target.value)} />
        </Field>

        <Field label="Authorisation Date" hint="When was permission granted?">
          <input type="date" value={t.auth_date||new Date().toISOString().split("T")[0]} onChange={e=>set("auth_date",e.target.value)} />
        </Field>
      </div>

      {/* Legal agreement */}
      <div style={{ background:agreed?C.accentDim:C.warnDim,border:`1.5px solid ${agreed?C.accent+"50":C.warn+"50"}`,borderRadius:10,padding:"16px 18px",marginBottom:28,transition:"all .3s" }}>
        <label style={{ display:"flex",gap:14,cursor:"pointer",alignItems:"flex-start" }}>
          <div onClick={()=>setAgreed(p=>!p)} style={{ width:22,height:22,borderRadius:4,border:`2px solid ${agreed?C.accent:C.warn}`,background:agreed?C.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginTop:1,transition:"all .2s",cursor:"pointer" }}>
            {agreed&&<span style={{ fontSize:13,color:C.bg,fontWeight:900 }}>✓</span>}
          </div>
          <div style={{ fontSize:13,color:C.text,lineHeight:1.6 }}>
            <strong style={{ color:agreed?C.accent:C.warn }}>I confirm I have explicit written authorisation</strong> to perform security testing on the target system listed above. I understand that scanning systems without permission is illegal under the Electronic Communications and Transactions Act (South Africa) and similar laws worldwide. I take full legal responsibility for the use of these scan results.
          </div>
        </label>
        {errors.agreed && <div style={{ fontSize:11,color:C.critical,marginTop:8,fontFamily:"'Share Tech Mono',monospace" }}>⚠ {errors.agreed}</div>}
      </div>

      <div style={{ display:"flex",gap:12 }}>
        <NextBtn onClick={async ()=>{
          if (!validate()) return;
          try {
            const t = data.target || {};
            const ipType_val = t.ip || "";
            const res = await wapi("/api/targets", { method:"POST", body: JSON.stringify({
              name: t.name,
              ip_address: ipType_val.match(/^\d+\.\d+\.\d+\.\d+/) ? ipType_val : null,
              hostname: !ipType_val.match(/^\d+\.\d+\.\d+\.\d+/) ? ipType_val : (t.hostname || null),
              description: t.description || null,
              authorized_by: t.authorized_by,
              auth_date: t.auth_date || new Date().toISOString().split("T")[0],
            })});
            if (res.id) {
              setData(p => ({ ...p, target: { ...(p.target||{}), savedId: res.id } }));
            }
          } catch(e) { /* non-blocking */ }
          onNext();
        }}>Continue →</NextBtn>
        <NextBtn variant="outline" onClick={onBack}>← Back</NextBtn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  STEP 4 — CONFIGURE SCAN
// ═══════════════════════════════════════════════════════════
function StepScan({ data, setData, onNext, onBack }) {
  const [launched, setLaunched]   = useState(false);
  const [progress, setProgress]   = useState(0);
  const [logLines, setLogLines]   = useState([]);
  const [done, setDone]           = useState(false);
  const logRef = useRef(null);

  const scanType  = data.scanType  || "full";
  const modules   = Array.isArray(data.modules) ? data.modules : ["os","version","vuln"];

  const setScanType = (t) => setData(p=>({...p, scanType:t}));
  const toggleMod   = (m) => setData(p=>({ ...p, modules: (p.modules||["os","version","vuln"]).includes(m) ? (p.modules||[]).filter(x=>x!==m) : [...(p.modules||[]),m] }));

  const LOG = [
    `Starting ${scanType} scan on ${data.target?.ip||"192.168.1.1"}...`,
    "Host is up (0.0018s latency)",
    `Running ${scanType==="quick"?"top-1000":"full 65535"} port scan...`,
    "Port 22/tcp    open  SSH      OpenSSH 8.2",
    "Port 80/tcp    open  HTTP     Apache 2.4.52",
    "Port 443/tcp   open  HTTPS    nginx 1.22",
    "Port 3306/tcp  open  MySQL    8.0.32",
    "Port 8080/tcp  open  HTTP-Alt Apache Tomcat 9",
    modules.includes("os")?"OS detection: Linux 5.15 (Ubuntu 22.04) — 96%":"",
    modules.includes("version")?"Service version detection complete":"",
    modules.includes("vuln")?"Running vulnerability scripts (NSE)...":"",
    "[CRITICAL] CVE-2021-44228 Log4Shell detected — port 8080",
    "[HIGH]     CVE-2018-15473 OpenSSH enumeration — port 22",
    "[MEDIUM]   SSL certificate expiring in 12 days — port 443",
    `Scan complete. Duration: ${scanType==="quick"?"47":"312"}s`,
  ].filter(Boolean);

  const [scanResult, setScanResult] = useState(null);
  const [realScanId, setRealScanId] = useState(null);

  const launch = async () => {
    setLaunched(true); setProgress(0); setLogLines([]);
    setLogLines([
      `Starting ${scanType} scan on ${data.target?.ip || "target"}...`,
      "Connecting to scan engine...",
      "Queuing Nmap scan...",
    ]);
    const targetId = data.target?.savedId;
    if (targetId) {
      try {
        const res = await wapi("/api/scans", { method:"POST", body: JSON.stringify({
          target_id: targetId,
          scan_type: scanType,
          modules: modules,
        })});
        if (res.id) {
          setRealScanId(res.id);
          setLogLines(p => [...p, `Scan queued (${res.id.slice(0,8)})`, "Nmap running — this may take several minutes..."]);
          setProgress(8);
        }
      } catch(e) {
        setLogLines(p => [...p, "Warning: Could not reach scan engine. Running demo mode."]);
        // Fall through to demo simulation below
      }
    }
    if (!targetId) {
      // No saved target ID — run demo simulation
      let i = 0;
      const iv = setInterval(() => {
        if (i < LOG.length) { setLogLines(p=>[...p, LOG[i]]); setProgress(Math.round((i / LOG.length) * 100)); i++; }
        else { clearInterval(iv); setProgress(100); setDone(true); }
      }, 420);
    }
  };

  // Poll real scan
  useEffect(() => {
    if (!realScanId || done) return;
    const iv = setInterval(async () => {
      try {
        const scan = await wapi(`/api/scans/${realScanId}`);
        if (scan.status === "completed") {
          clearInterval(iv);
          setProgress(100);
          setDone(true);
          setScanResult(scan);
          const lines = [
            `Scan complete — duration: ${scan.scan_duration || "?"}s`,
            `Open ports: ${scan.open_ports || 0}`,
            `Vulnerabilities found: ${scan.total_vulns || 0}`,
          ];
          if (scan.critical_count > 0) lines.push(`[CRITICAL] ${scan.critical_count} critical vulnerabilit${scan.critical_count===1?"y":"ies"} detected!`);
          if (scan.high_count > 0)     lines.push(`[HIGH]     ${scan.high_count} high vulnerabilit${scan.high_count===1?"y":"ies"} detected`);
          lines.push(`Risk score: ${scan.risk_score || 0}/10`);
          lines.push("Scan complete.");
          setLogLines(p => [...p, ...lines]);
          setData(p => ({ ...p, scanResult: scan }));
        } else if (scan.status === "failed") {
          clearInterval(iv);
          setLogLines(p => [...p, "Scan failed — check target is reachable."]);
          setDone(true);
        } else {
          setProgress(p => Math.min(p + 5, 90));
        }
      } catch(e) { /* ignore */ }
    }, 4000);
    return () => clearInterval(iv);
  }, [realScanId, done]);

  useEffect(()=>{ if(logRef.current) logRef.current.scrollTop=logRef.current.scrollHeight; },[logLines]);

  const SCAN_TYPES = [
    { id:"quick",   icon:"⚡", label:"Quick",   sub:"Top 1000 ports · ~2 min",    color:C.green  },
    { id:"full",    icon:"◎",  label:"Full",    sub:"All 65535 ports · ~15 min",  color:C.accent },
    { id:"vuln",    icon:"⚠",  label:"Vuln",    sub:"CVE detection · ~20 min",    color:C.warn   },
    { id:"stealth", icon:"◈",  label:"Stealth", sub:"Low-noise SYN · ~10 min",    color:C.blue   },
  ];

  const MODS = [
    { id:"os",     label:"OS Detection",           desc:"Fingerprint the operating system" },
    { id:"version",label:"Version Detection",      desc:"Identify service versions" },
    { id:"vuln",   label:"CVE Vuln Scripts",        desc:"Detect known CVEs via NSE" },
    { id:"ssl",    label:"SSL/TLS Analysis",        desc:"Check certificate and ciphers" },
    { id:"web",    label:"Web App Scan",            desc:"HTTP headers, methods, titles" },
  ];

  return (
    <div className="step-enter" style={{ maxWidth:780,margin:"0 auto" }}>
      <div style={{ marginBottom:28 }}>
        <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:C.accent,letterSpacing:3,marginBottom:10 }}>STEP 04 / 05</div>
        <h2 style={{ fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:38,letterSpacing:2,marginBottom:8 }}>CONFIGURE YOUR SCAN</h2>
        <p style={{ color:C.dim,fontSize:15 }}>Choose how to scan <strong style={{ color:C.text }}>{data.target?.name}</strong> ({data.target?.ip}).</p>
      </div>

      {!launched ? (
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:16 }}>
          {/* Left: config */}
          <div>
            <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:22,marginBottom:14 }}>
              <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:C.dim,letterSpacing:2,marginBottom:14 }}>SCAN TYPE</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
                {SCAN_TYPES.map(s=>(
                  <button key={s.id} onClick={()=>setScanType(s.id)} style={{ background:scanType===s.id?s.color+"20":C.surface,border:`1.5px solid ${scanType===s.id?s.color:C.border}`,borderRadius:8,padding:"11px 12px",cursor:"pointer",textAlign:"left",transition:"all .15s" }}>
                    <div style={{ fontSize:18,marginBottom:5,color:scanType===s.id?s.color:C.dim }}>{s.icon}</div>
                    <div style={{ fontSize:13,fontWeight:700,color:scanType===s.id?s.color:C.text,marginBottom:2 }}>{s.label}</div>
                    <div style={{ fontSize:10,color:C.muted }}>{s.sub}</div>
                  </button>
                ))}
              </div>
            </div>

            <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:22 }}>
              <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:C.dim,letterSpacing:2,marginBottom:14 }}>MODULES</div>
              {MODS.map(m=>(
                <label key={m.id} style={{ display:"flex",alignItems:"center",gap:12,marginBottom:12,cursor:"pointer",padding:"8px 10px",borderRadius:6,background:modules.includes(m.id)?C.accentDim:"transparent",border:`1px solid ${modules.includes(m.id)?C.accent+"30":C.border+"00"}`,transition:"all .15s" }}>
                  <div onClick={()=>toggleMod(m.id)} style={{ width:18,height:18,borderRadius:3,border:`2px solid ${modules.includes(m.id)?C.accent:C.muted}`,background:modules.includes(m.id)?C.accent:"transparent",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,transition:"all .15s" }}>
                    {modules.includes(m.id)&&<span style={{ fontSize:11,color:C.bg,fontWeight:900 }}>✓</span>}
                  </div>
                  <div>
                    <div style={{ fontSize:13,fontWeight:600,color:modules.includes(m.id)?C.text:C.dim }}>{m.label}</div>
                    <div style={{ fontSize:11,color:C.muted }}>{m.desc}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Right: summary + launch */}
          <div>
            <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:22,marginBottom:14 }}>
              <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:C.dim,letterSpacing:2,marginBottom:16 }}>SCAN SUMMARY</div>
              {[
                ["Target",   data.target?.name],
                ["Address",  data.target?.ip],
                ["Type",     scanType.toUpperCase()],
                ["Modules",  modules.length+" selected"],
                ["Est. Time",scanType==="quick"?"~2 min":scanType==="full"?"~15 min":scanType==="vuln"?"~20 min":"~10 min"],
              ].map(([k,v])=>(
                <div key={k} style={{ display:"flex",justifyContent:"space-between",padding:"7px 0",borderBottom:`1px solid ${C.border}22`,fontSize:13 }}>
                  <span style={{ color:C.dim }}>{k}</span>
                  <span style={{ color:C.text,fontFamily:"'Share Tech Mono',monospace",fontSize:12 }}>{v}</span>
                </div>
              ))}
            </div>

            <div style={{ background:C.accentDim,border:`1.5px solid ${C.accent}50`,borderRadius:12,padding:22,textAlign:"center",animation:"glowPulse 3s ease infinite" }}>
              <div style={{ fontSize:32,marginBottom:12,color:C.accent }}>◎</div>
              <div style={{ fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:22,letterSpacing:2,color:C.accent,marginBottom:8 }}>READY TO LAUNCH</div>
              <div style={{ fontSize:12,color:C.dim,marginBottom:20,lineHeight:1.6 }}>Your first scan will run immediately.<br />Results appear in your dashboard.</div>
              <NextBtn onClick={launch} style={{ width:"100%" }}>⊕ LAUNCH SCAN</NextBtn>
            </div>
          </div>
        </div>
      ) : (
        /* Terminal output */
        <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,overflow:"hidden",animation:"fadeIn .3s ease" }}>
          <div style={{ background:C.panel,padding:"12px 20px",borderBottom:`1px solid ${C.border}`,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
            <div style={{ display:"flex",gap:7 }}>
              {["#ff5f57","#febc2e","#28c840"].map(c=><div key={c} style={{ width:11,height:11,borderRadius:"50%",background:c }} />)}
            </div>
            <span style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:C.dim }}>sentinel-scan — {data.target?.ip}</span>
            {!done && <span style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:C.accent,animation:"pulse 1s infinite" }}>● SCANNING</span>}
            {done  && <span style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:11,color:C.green }}>● COMPLETE</span>}
          </div>

          {/* Progress */}
          <div style={{ padding:"10px 20px",borderBottom:`1px solid ${C.border}`,background:C.surface }}>
            <div style={{ display:"flex",justifyContent:"space-between",marginBottom:5 }}>
              <span style={{ fontSize:11,color:C.dim,fontFamily:"'Share Tech Mono',monospace" }}>SCAN PROGRESS</span>
              <span style={{ fontSize:11,color:done?C.green:C.accent,fontFamily:"'Share Tech Mono',monospace",fontWeight:700 }}>{progress}%</span>
            </div>
            <div style={{ height:4,background:C.border,borderRadius:2 }}>
              <div style={{ height:"100%",width:`${progress}%`,background:done?`linear-gradient(90deg,${C.accentMid},${C.green})`:`linear-gradient(90deg,${C.accentMid},${C.accent})`,borderRadius:2,transition:"width .4s",boxShadow:done?`0 0 10px ${C.green}44`:`0 0 10px ${C.accentGlow}` }} />
            </div>
          </div>

          {/* Log output */}
          <div ref={logRef} style={{ padding:20,height:280,overflowY:"auto",fontFamily:"'Share Tech Mono',monospace",fontSize:12,lineHeight:2.1 }}>
            {logLines.map((line,i)=>typeof line !== "string" ? null : (
              <div key={i} style={{ color:line.includes("[CRITICAL]")?C.critical:line.includes("[HIGH]")?C.warn:line.includes("[MEDIUM]")?C.gold:line.includes("open")?C.accent:line.includes("complete")||line.includes("Complete")?C.green:C.dim,animation:"fadeUp .2s ease" }}>
                <span style={{ color:C.muted,marginRight:8,fontSize:10 }}>{String(i).padStart(2,"0")}›</span>{line}
              </div>
            ))}
            {!done && <span style={{ color:C.accent,animation:"blink 1s infinite" }}>█</span>}
          </div>
        </div>
      )}

      <div style={{ display:"flex",gap:12,marginTop:24,flexWrap:"wrap" }}>
        {done      && <NextBtn onClick={onNext}>View Results →</NextBtn>}
        {!launched && <NextBtn variant="outline" onClick={onBack}>← Back</NextBtn>}
        {!launched && <NextBtn variant="outline" onClick={onNext}>Skip for now →</NextBtn>}
        {launched && !done && <><span style={{ fontSize:13,color:C.dim,display:"flex",alignItems:"center",gap:8 }}><div style={{ width:14,height:14,border:`2px solid ${C.border}`,borderTopColor:C.accent,borderRadius:"50%",animation:"spin .7s linear infinite" }} />Scanning...</span><NextBtn variant="outline" onClick={onNext}>Skip →</NextBtn></>}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  STEP 5 — COMPLETE
// ═══════════════════════════════════════════════════════════
function StepComplete({ data, onFinish }) {
  const [shown, setShown] = useState(false);
  useEffect(()=>{ setTimeout(()=>setShown(true),300); },[]);

  const sr = data.scanResult || {};
  const RESULTS = {
    critical:  sr.critical_count  || 0,
    high:      sr.high_count      || 0,
    medium:    sr.medium_count    || 0,
    low:       sr.low_count       || 0,
    openPorts: sr.open_ports      || 0,
    duration:  sr.scan_duration ? `${sr.scan_duration}s` : "—",
    riskScore: sr.risk_score      || 0,
  };
  const riskColor = RESULTS.riskScore>=9?C.critical:RESULTS.riskScore>=7?C.warn:RESULTS.riskScore>=4?C.gold:C.green;
  const riskLabel = RESULTS.riskScore>=9?"CRITICAL":RESULTS.riskScore>=7?"HIGH":RESULTS.riskScore>=4?"MEDIUM":"LOW";

  return (
    <div className="step-enter" style={{ maxWidth:680,margin:"0 auto",textAlign:"center" }}>
      {/* Success icon */}
      <div style={{ marginBottom:40 }}>
        <div style={{ width:100,height:100,borderRadius:"50%",background:`${C.green}20`,border:`2px solid ${C.green}60`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:42,margin:"0 auto 24px",animation:`pop .5s cubic-bezier(.34,1.56,.64,1) both, glowPulse 2s .5s ease infinite`,color:C.green,boxShadow:`0 0 40px ${C.green}30` }}>✓</div>
        <h1 style={{ fontFamily:"'Rajdhani',sans-serif",fontWeight:700,fontSize:48,letterSpacing:3,color:C.text,marginBottom:8,lineHeight:1 }}>
          YOU'RE ALL SET!
        </h1>
        <p style={{ fontSize:16,color:C.dim,marginTop:12 }}>
          Your first scan of <strong style={{ color:C.text }}>{data.target?.name}</strong> is complete.<br />Here's what we found.
        </p>
      </div>

      {/* Risk score big display */}
      <div style={{ background:riskColor+"12",border:`1.5px solid ${riskColor}40`,borderRadius:16,padding:"28px 40px",marginBottom:24,animation:`fadeUp .5s .2s both` }}>
        <div style={{ fontSize:11,color:C.dim,fontFamily:"'Share Tech Mono',monospace",letterSpacing:3,marginBottom:8 }}>OVERALL RISK SCORE</div>
        <div style={{ fontFamily:"'Rajdhani',sans-serif",fontSize:88,fontWeight:800,color:riskColor,lineHeight:1,textShadow:`0 0 30px ${riskColor}50` }}>
          {RESULTS.riskScore}
          <span style={{ fontSize:32,color:C.dim,fontWeight:400 }}>/10</span>
        </div>
        <div style={{ marginTop:8 }}>
          <span style={{ background:riskColor+"25",color:riskColor,border:`1px solid ${riskColor}50`,borderRadius:4,padding:"4px 16px",fontSize:12,fontFamily:"'Share Tech Mono',monospace",fontWeight:700,letterSpacing:2 }}>{riskLabel} RISK</span>
        </div>
      </div>

      {/* Finding counts */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10,marginBottom:24,animation:`fadeUp .5s .3s both` }}>
        {[
          { label:"Critical",   val:RESULTS.critical, color:C.critical },
          { label:"High",       val:RESULTS.high,     color:C.warn     },
          { label:"Medium",     val:RESULTS.medium,   color:C.gold     },
          { label:"Open Ports", val:RESULTS.openPorts,color:C.accent   },
        ].map((f,i)=>(
          <div key={i} style={{ background:C.card,border:`1px solid ${C.border}`,borderTop:`2px solid ${f.color}`,borderRadius:8,padding:16,textAlign:"center" }}>
            <div style={{ fontFamily:"'Rajdhani',sans-serif",fontSize:36,fontWeight:700,color:f.color,lineHeight:1 }}>{f.val}</div>
            <div style={{ fontSize:11,color:C.dim,marginTop:4,fontFamily:"'Share Tech Mono',monospace",letterSpacing:1 }}>{f.label.toUpperCase()}</div>
          </div>
        ))}
      </div>

      {/* What's next */}
      <div style={{ background:C.card,border:`1px solid ${C.border}`,borderRadius:12,padding:22,marginBottom:28,textAlign:"left",animation:`fadeUp .5s .4s both` }}>
        <div style={{ fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:C.dim,letterSpacing:2,marginBottom:14 }}>WHAT HAPPENS NEXT</div>
        {[
          { icon:"⊟", text:"A full PDF report is being generated and will be emailed to you shortly.", color:C.accent },
          { icon:"⚠", text:`${RESULTS.critical+RESULTS.high} critical/high vulnerabilities need immediate attention — see full details in your dashboard.`, color:C.warn },
          { icon:"◎", text:"Schedule regular scans to catch new vulnerabilities as they emerge.", color:C.green },
          { icon:"◈", text:"Your PayFast subscription billing starts after your 14-day free trial.", color:C.gold },
        ].map((item,i)=>(
          <div key={i} style={{ display:"flex",gap:14,padding:"10px 0",borderBottom:`1px solid ${C.border}22`,alignItems:"flex-start" }}>
            <span style={{ fontSize:18,color:item.color,marginTop:1,flexShrink:0 }}>{item.icon}</span>
            <span style={{ fontSize:13,color:C.dim,lineHeight:1.6 }}>{item.text}</span>
          </div>
        ))}
      </div>

      {/* Plan reminder */}
      <div style={{ background:C.accentDim,border:`1px solid ${C.accent}40`,borderRadius:8,padding:"12px 18px",marginBottom:28,fontSize:13,color:C.text,textAlign:"left",animation:`fadeUp .5s .5s both` }}>
        <span style={{ color:C.accent }}>◈ </span>
        You're on the <strong style={{ color:C.accent }}>{PLANS.find(p=>p.slug===data.plan)?.name} Plan</strong> trial.
        You have <strong>{PLANS.find(p=>p.slug===data.plan)?.targets === -1 ? "unlimited" : PLANS.find(p=>p.slug===data.plan)?.targets}</strong> targets and
        <strong> {PLANS.find(p=>p.slug===data.plan)?.scans === -1 ? " unlimited" : ` ${PLANS.find(p=>p.slug===data.plan)?.scans}`}</strong> scans/month available.
      </div>

      <div style={{ display:"flex",gap:12,justifyContent:"center",animation:`fadeUp .5s .6s both` }}>
        <NextBtn onClick={onFinish} style={{ minWidth:260 }}>Go to Dashboard →</NextBtn>
      </div>

      <div style={{ marginTop:20,fontSize:12,color:C.muted,animation:`fadeUp .5s .7s both` }}>
        Questions? Email us at <span style={{ color:C.accent }}>support@sentinelpentest.co.za</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  TICKER (bottom status bar)
// ═══════════════════════════════════════════════════════════
function Ticker() {
  const items = ["SENTINEL PENTEST SUITE","AUTHORISED SCANS ONLY","POWERED BY NMAP + OPENVAS","PAYFAST SECURE BILLING","CVE DETECTION ACTIVE","ISO 27001 COMPLIANCE READY","ENCRYPTED RESULTS STORAGE"];
  const str   = items.join("   ·   ") + "   ·   " + items.join("   ·   ");
  return (
    <div style={{ position:"fixed",bottom:0,left:0,right:0,height:28,background:C.panel,borderTop:`1px solid ${C.border}`,overflow:"hidden",zIndex:100,display:"flex",alignItems:"center" }}>
      <div style={{ display:"inline-block",whiteSpace:"nowrap",animation:"ticker 40s linear infinite",fontFamily:"'Share Tech Mono',monospace",fontSize:10,color:C.muted,letterSpacing:2 }}>
        {str}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════
//  ROOT
// ═══════════════════════════════════════════════════════════
export default function OnboardingWizard({ user, onComplete }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({
    full_name:  user?.full_name  || "",
    company:    user?.company    || "",
    plan:       "business",
    modules:    ["os","version","vuln"],
  });

  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const TOTAL = STEPS.length;

  return (
    <>
      <style>{CSS}</style>
      <GridBg />
      <TopProgress step={step} total={TOTAL} />
      <StepNav current={step} />
      <Ticker />

      {/* Main scrollable area */}
      <div style={{ marginLeft:220,height:"100vh",paddingBottom:48,paddingTop:60,overflowY:"auto",position:"relative",zIndex:10 }}>
        <div style={{ padding:"40px 60px 80px" }}>
          {step===0 && <StepWelcome  data={data}                                    onNext={next} />}
          {step===1 && <StepProfile  data={data} setData={setData} onBack={back}    onNext={next} />}
          {step===2 && <StepPlan     data={data} setData={setData} onBack={back}    onNext={next} />}
          {step===3 && <StepTarget   data={data} setData={setData} onBack={back}    onNext={next} />}
          {step===4 && <StepScan     data={data} setData={setData} onBack={back}    onNext={next} />}
          {step===5 && <StepComplete data={data}                                   onFinish={()=>onComplete?.(data)} />}
        </div>
      </div>
    </>
  );
}
